import type { Customer, PopulatedOrgMember, Task, TaskStatus, User } from "@api/types";
import { getInitials } from "@lib/utils";
import { DateTime } from "luxon";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { htmlToText } from "@/lib/html-text";
import { cn } from "@/lib/utils";

/** Task with `populate: "customer user"` applied by the API */
export type PopulatedTask = Omit<Task, "customer" | "user"> & {
  customer?: Customer;
  user?: User;
};

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  todo: "To do",
  in_progress: "In progress",
  snoozed: "Snoozed",
  completed: "Done",
};

export function getUserName(user?: User | null): string {
  if (!user) return "Unassigned";
  return [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;
}

/**
 * Descriptions are stored as rich text (tiptap HTML); collapse to a single
 * plain-text line for list rows and board cards. Plain-text descriptions from
 * before the rich editor pass through unchanged.
 */
export function descriptionSnippet(description?: string | null): string {
  if (!description) return "";
  return htmlToText(description).replace(/\s+/g, " ").trim();
}

export function isTaskOverdue(task: Pick<Task, "status" | "dueDate">): boolean {
  if (!task.dueDate || task.status === "completed") return false;
  return new Date(task.dueDate).getTime() < Date.now();
}

/**
 * Due date with context: always leads with "Due" so the timestamp reads as a
 * deadline, then the friendliest calendar phrase — "Due today, 5:00 PM",
 * "Due tomorrow", "Due Friday", "Due Jul 20".
 */
export function dueDateLabel(dueDate: string | Date): string {
  const date = DateTime.fromJSDate(new Date(dueDate));
  const now = DateTime.now();
  const days = date.startOf("day").diff(now.startOf("day"), "days").days;
  if (days === 0) return `Due today, ${date.toFormat("h:mm a")}`;
  if (days === 1) return "Due tomorrow";
  if (days === -1) return "Due yesterday";
  if (days > 1 && days < 7) return `Due ${date.toFormat("cccc")}`;
  return `Due ${date.toFormat(date.year === now.year ? "MMM d" : "MMM d, yyyy")}`;
}

/** Wake date with context — "Snoozed until 9 PM", "Snoozed until tomorrow, 9 AM". */
export function snoozedUntilLabel(snoozedUntil: string | Date): string {
  const date = DateTime.fromJSDate(new Date(snoozedUntil));
  const now = DateTime.now();
  const days = date.startOf("day").diff(now.startOf("day"), "days").days;
  // On-the-hour times render compact ("9 AM") so badges stay short.
  const time = date.toFormat(date.minute === 0 ? "h a" : "h:mm a");
  if (days === 0) return `Snoozed until ${time}`;
  if (days === 1) return `Snoozed until tomorrow, ${time}`;
  if (days > 1 && days < 7) return `Snoozed until ${date.toFormat("cccc")}`;
  return `Snoozed until ${date.toFormat(date.year === now.year ? "MMM d" : "MMM d, yyyy")}`;
}

/**
 * Translate an update-request body (API shapes: user id, Date objects, nulls)
 * into the populated shapes the UI renders, so optimistic cache patches look
 * identical to what the next fetch returns.
 */
export function optimisticTaskPatch(
  body: Record<string, unknown>,
  members?: PopulatedOrgMember[],
): Partial<PopulatedTask> {
  const patch: Record<string, unknown> = { ...body };
  if (body.user !== undefined) {
    patch.user = body.user
      ? members?.find((member) => member.user.id === body.user)?.user
      : undefined;
  }
  for (const key of ["dueDate", "snoozedUntil"] as const) {
    if (body[key] instanceof Date) patch[key] = (body[key] as Date).toISOString();
    if (body[key] === null) patch[key] = undefined;
  }
  if (body.status === "completed") patch.completedAt = new Date().toISOString();
  // Leaving the snoozed state clears the wake date.
  if (body.status && body.status !== "snoozed") patch.snoozedUntil = undefined;
  return patch as Partial<PopulatedTask>;
}

export function TaskUserAvatar({ user, className }: { user?: User | null; className?: string }) {
  const name = getUserName(user);
  return (
    <Avatar className={cn("size-5 shrink-0 border", className)}>
      <AvatarFallback className="bg-muted text-muted-foreground text-[9px] font-semibold">
        {user ? getInitials(name) : "?"}
      </AvatarFallback>
    </Avatar>
  );
}
